import type React from 'react'
import {
  Grid,
  GridItem,
  Text,
  Box,
  TextField,
  Autocomplete,
  type AutocompleteProps,
  Select,
} from '@truepill/react-capsule'
import { format } from 'date-fns'
import { enUS } from 'date-fns/locale'
import styled from 'styled-components'
import { capsuleGreyColor, contrastBackgroundColor, linkColor, primaryColorLight } from 'styles/styleVariables'
import { TPOSCard } from './Card'

type CellContent = string | number | Date | React.ReactElement | undefined

type CellData = {
  label: string
  prescribed?: { value: CellContent }
  dispensed?: { value: CellContent }
  fullWidth?: boolean
  isDate?: boolean
  testId?: string
}

type PV1CardProps = {
  title: string | React.ReactElement
  rows?: CellData[]
  children?: React.ReactNode
}

const formatValue = (val: CellContent | null | undefined, row: CellData) => {
  if (!val) return 'None provided'

  if (row?.isDate) {
    return format(new Date(val as string), 'MMM d, yyyy', { locale: enUS })
  }

  return val
}

const PV1Card = ({ title, children, rows }: PV1CardProps): JSX.Element => {
  return (
    <TPOSCard header={title}>
      <Box>
        <Row data-testid="headers" rowSpan={2}>
          <Cell $borderBottom />

          <Cell $borderBottom>
            <ColumnLabel>PRESCRIBED</ColumnLabel>
          </Cell>

          <Cell $borderBottom $paddingLeft>
            <ColumnLabel>DISPENSED</ColumnLabel>
          </Cell>
        </Row>

        {children
          ? children
          : rows?.map((row, index) => {
              const isFullWidth = row.fullWidth
              const isLastRow = index === rows.length - 1
              return (
                <Row key={index} data-test-row={row.testId} rowSpan={isFullWidth ? 1 : 2}>
                  <Cell $borderBottom={!isLastRow}>
                    <RowLabel bold>{row.label}</RowLabel>
                  </Cell>

                  <Cell $borderBottom={!isLastRow} $borderRight={!isFullWidth}>
                    <CellValue>{formatValue(row.prescribed?.value, row)}</CellValue>
                  </Cell>

                  {!isFullWidth && (
                    <Cell $borderBottom={!isLastRow} $paddingLeft>
                      <CellValue>{formatValue(row.dispensed?.value, row)}</CellValue>
                    </Cell>
                  )}
                </Row>
              )
            })}
      </Box>
    </TPOSCard>
  )
}

const Row = ({
  rowSpan = 1,
  ...props
}: Parameters<typeof Grid>[0] & {
  rowSpan?: 1 | 2
}) => {
  const rowSpanOptions = {
    '1': '3fr 9fr',
    '2': '3fr 4.5fr 4.5fr',
  }
  return (
    <Grid
      spacing="none"
      css={{
        gridTemplateColumns: rowSpanOptions[rowSpan],
      }}
      {...props}
    />
  )
}

const ColumnLabel = styled(Text)`
  color: ${capsuleGreyColor};
  font-size: 11px;
  font-weight: 700;
  line-height: 1rem;
`

const RowLabel = styled(Text)`
  font-size: 0.875rem;
  line-height: 1.25rem;
`

const Cell = styled(GridItem)<{
  $borderBottom?: boolean
  $borderRight?: boolean
  $paddingLeft?: boolean
}>`
  padding: 0.375rem 0;
  ${({ $borderBottom }) => $borderBottom && `border-bottom: 0.125rem solid ${contrastBackgroundColor};`}
  ${({ $borderRight }) => $borderRight && `border-right: 0.125rem solid ${contrastBackgroundColor};`}
  ${({ $paddingLeft }) => $paddingLeft && `padding-left: 0.5rem;`}
`

const CellValue = styled(Box).attrs(() => ({
  'data-testid': 'value',
}))`
  font-size: 0.875rem;
  font-family: var(--cap-fonts-base);
  padding-right: 0.75rem;
  a {
    text-decoration: underline;
    color: ${linkColor};
    text-underline-offset: 2px;
    font-size: 0.875rem;
  }
`
export { Row, CellValue, Cell, RowLabel, ColumnLabel, PV1Card, type CellData, type CellContent, type PV1CardProps }

export const TextInput = styled(TextField)`
  background-color: ${primaryColorLight};
  border: 0 none;
  box-shadow: none;
  height: 32px;
  padding: 0.5rem;
`

export function TextAutocomplete<T>(props: AutocompleteProps<T>) {
  return (
    <Autocomplete
      {...props}
      css={{
        '&>div>div:first-child': {
          backgroundColor: primaryColorLight,
          border: '0 none',
          boxShadow: 'none',
          height: '32px',
          padding: '0',
          borderRadius: 'var(--cap-radii-sm)',
        },
        '&>div>div>input': {
          height: 32,
          border: '0 none',
          background: 'none',
        },
        '&>div>div>button': {
          height: 32,
        },
      }}
    />
  )
}

export function SelectInput(props: any) {
  return (
    <Select
      {...props}
      css={{
        top: '-3px',
        '&>div>button': {
          backgroundColor: primaryColorLight,
          border: '0 none',
          height: 32,
        },
      }}
    />
  )
}
